import { usePage } from "@inertiajs/react"
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"

import type { SharedData } from "@/types"

const STORAGE_KEY = "notifications_unread_count"
const STORAGE_TS_KEY = "notifications_unread_ts"
const FRESHNESS_MS = 60_000

interface NotificationsContextType {
  unreadCount: number
  setUnreadCount: (count: number) => void
  decrementUnreadCount: () => void
}

const NotificationsContext = createContext<NotificationsContextType | null>(
  null,
)

function getStoredCount(): { count: number; fresh: boolean } | null {
  const raw = sessionStorage.getItem(STORAGE_KEY)
  const ts = sessionStorage.getItem(STORAGE_TS_KEY)
  if (raw === null) return null
  const count = parseInt(raw, 10)
  if (Number.isNaN(count)) return null
  const fresh = ts !== null && Date.now() - parseInt(ts, 10) < FRESHNESS_MS
  return { count, fresh }
}

function storeCount(count: number) {
  sessionStorage.setItem(STORAGE_KEY, String(count))
  sessionStorage.setItem(STORAGE_TS_KEY, String(Date.now()))
}

export function NotificationProvider({ children }: { children: ReactNode }) {
  const page = usePage<SharedData>()
  const serverCount = page.props.auth?.notifications?.unread_count ?? 0
  const initialized = useRef(false)

  const [unreadCount, setUnreadCountRaw] = useState<number>(() => {
    const stored = getStoredCount()
    if (stored?.fresh) return stored.count
    return serverCount
  })

  const setUnreadCount = useCallback((count: number) => {
    setUnreadCountRaw(count)
    storeCount(count)
  }, [])

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true
      const stored = getStoredCount()
      if (!stored?.fresh) {
        setUnreadCount(serverCount)
      }
    }
  }, [])

  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<{ unread_count?: number }>
      if (typeof ce.detail?.unread_count === "number") {
        setUnreadCount(ce.detail.unread_count)
      }
    }
    window.addEventListener("notifications:updated", handler)
    return () => window.removeEventListener("notifications:updated", handler)
  }, [])

  useEffect(() => {
    const handler = () => {
      setUnreadCountRaw((prev) => {
        const next = prev + 1
        storeCount(next)
        return next
      })
    }
    window.addEventListener("notifications:increment", handler)
    return () => window.removeEventListener("notifications:increment", handler)
  }, [])

  const decrementUnreadCount = useCallback(() => {
    setUnreadCountRaw((prev) => {
      const next = Math.max(0, prev - 1)
      storeCount(next)
      return next
    })
  }, [])

  return (
    <NotificationsContext.Provider
      value={{ unreadCount, setUnreadCount, decrementUnreadCount }}
    >
      {children}
    </NotificationsContext.Provider>
  )
}

export function useNotifications() {
  const context = useContext(NotificationsContext)
  if (!context)
    throw new Error("useNotifications must be used within NotificationProvider")
  return context
}
